import { cn } from '@/lib/utils';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { ChangeSummaryItemVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import {
  isZeroMoneyList,
  moneyListDirection,
} from '@/features/dashboard/bookingChanges/utils/money.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import { summaryImpactValues } from './changePackageCopy.ts';
import {
  ChangeSummaryTableVariant,
  summaryIndentClass,
  summaryRowClass,
} from './changeSummaryTableLayout.ts';

/**
 * Kwota delty: ujemna na zielono (taniej), dodatnia czarna, zerowa wyszarzona.
 * Delta wielowalutowa o rozbieżnych znakach zostaje czarna — zielony znaczyłby
 * „taniej”, a tanieje wtedy tylko jedna z walut.
 */
const deltaToneClass = (delta: MoneyValueDto[]): string => {
  const direction = moneyListDirection(delta);
  if (direction === 0) {
    return 'font-normal text-neutral-500';
  }
  return direction === -1 ? 'text-success-300' : 'text-ink';
};

interface ChangeSummaryItemRowProps {
  item: ChangeSummaryItemVm;
  variant: ChangeSummaryTableVariant;
}

/**
 * Jedna zmiana: co, wartość przed i po, wpływ na kwotę.
 *
 * Kolejność w kodzie jest kolejnością kolumn — układ szeroki rozstawia komórki
 * sam, węższe wskazują pozycje wprost i zwalniają je z powrotem powyżej 600 px.
 * Wartość sprzed zmiany zostaje tylko w pełnym układzie: w węższym liczy się to,
 * czym pozycja **się staje** i ile to kosztuje.
 */
const ChangeSummaryItemRow = ({ item, variant }: ChangeSummaryItemRowProps) => (
  <div
    role="row"
    className={cn(
      summaryRowClass(variant),
      '@max-[700px]:py-3 @min-[700px]:items-center border-b border-neutral-100',
    )}
  >
    <div
      role="cell"
      className={cn(
        summaryIndentClass,
        '@max-[700px]:py-0 @max-[700px]:text-[13px] @max-[700px]:font-normal @max-[700px]:text-neutral-500 wrap-anywhere min-w-0 py-3.5 text-[15px] font-semibold text-ink',
      )}
    >
      {item.label}
    </div>
    <div
      role="cell"
      className={cn(
        summaryIndentClass,
        '@max-[700px]:hidden @min-[700px]:pl-0 wrap-anywhere min-w-0 py-3.5 text-[15px] text-neutral-400 line-through',
      )}
    >
      {item.before}
    </div>
    <div
      role="cell"
      className={cn(
        summaryIndentClass,
        '@min-[440px]:col-start-1 @min-[440px]:row-start-2 @min-[700px]:col-start-auto @min-[700px]:row-start-auto @max-[700px]:py-0 @min-[700px]:pl-0 wrap-anywhere min-w-0 py-3.5 text-[15px] font-semibold text-ink',
      )}
    >
      {item.after}
    </div>
    <div
      role="cell"
      className={cn(
        summaryIndentClass,
        '@min-[440px]:col-start-2 @min-[440px]:row-start-1 @min-[440px]:text-right @min-[700px]:col-start-auto @min-[700px]:row-start-auto @max-[700px]:py-0 @max-[700px]:text-[13px] @min-[440px]:pl-0 @min-[700px]:pl-0 @min-[700px]:text-right whitespace-nowrap py-3.5 text-left text-[15px] font-semibold tabular-nums',
        // Kreska trzyma wyrównanie kolumny kwot; w jednej kolumnie nie ma
        // czego wyrównywać, więc pozycja bez skutku finansowego milczy.
        isZeroMoneyList(item.delta) && '@max-[440px]:hidden',
        deltaToneClass(item.delta),
      )}
    >
      <MoneyAmountList
        values={summaryImpactValues(item.delta)}
        variant="signed"
      />
    </div>
  </div>
);

export default ChangeSummaryItemRow;
